import React from 'react';
import { SimpleCheckboxIcon } from './CustomIcons';

interface ReviewVerificationResultProps {
  result: {
    keyPointsCovered: {
      covered: string[];
      missing: string[];
      score: number;
    };
    additionalContent: {
      hasAdditional: boolean;
      correctness: number;
      insights: string[];
    };
    overallScore: number;
    feedback: string;
    recommendations: string[];
  };
  onClose: () => void;
}

export const ReviewVerificationResult: React.FC<ReviewVerificationResultProps> = ({
  result,
  onClose
}) => {
  const getScoreColor = (score: number) => {
    if (score >= 80) return 'excellent';
    if (score >= 60) return 'good';
    if (score >= 40) return 'fair';
    return 'poor';
  };

  const getScoreEmoji = (score: number) => {
    if (score >= 80) return '🎉';
    if (score >= 60) return '👍';
    if (score >= 40) return '👌';
    return '💪';
  };

  return (
    <div className="review-verification-overlay">
      <div className="review-verification-modal">
        <div className="verification-header">
          <h3>📊 Understanding Verification</h3>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="verification-content">
          {/* Overall Score */}
          <div className={`overall-score ${getScoreColor(result.overallScore)}`}>
            <div className="score-display">
              <span className="score-emoji">{getScoreEmoji(result.overallScore)}</span>
              <span className="score-number">{result.overallScore}%</span>
            </div>
            <div className="score-label">Overall Understanding</div>
          </div>

          {/* Key Points Coverage */}
          <div className="verification-section">
            <h4>🎯 Key Points Coverage</h4>
            <div className={`section-score ${getScoreColor(result.keyPointsCovered.score)}`}>
              {result.keyPointsCovered.score}%
            </div>
            
            {result.keyPointsCovered.covered.length > 0 && (
              <div className="covered-points">
                <strong><SimpleCheckboxIcon size={14} color="#28a745" /> Covered Points:</strong>
                <ul>
                  {result.keyPointsCovered.covered.map((point, index) => (
                    <li key={index} className="covered-point">{point}</li>
                  ))}
                </ul>
              </div>
            )}

            {result.keyPointsCovered.missing.length > 0 && (
              <div className="missing-points">
                <strong>❌ Missing Points:</strong>
                <ul>
                  {result.keyPointsCovered.missing.map((point, index) => (
                    <li key={index} className="missing-point">{point}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Additional Content */}
          {result.additionalContent.hasAdditional && (
            <div className="verification-section">
              <h4>💡 Additional Insights</h4>
              <div className={`section-score ${getScoreColor(result.additionalContent.correctness)}`}>
                {result.additionalContent.correctness}% Accuracy
              </div>
              
              {result.additionalContent.insights.length > 0 && (
                <div className="additional-insights">
                  <strong>🌟 Extra Insights You Shared:</strong>
                  <ul>
                    {result.additionalContent.insights.map((insight, index) => (
                      <li key={index} className="additional-insight">{insight}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Feedback */}
          <div className="verification-section">
            <h4>💬 Detailed Feedback</h4>
            <div className="feedback-text">
              {result.feedback}
            </div>
          </div>

          {/* Recommendations */}
          {result.recommendations.length > 0 && (
            <div className="verification-section">
              <h4>🚀 Recommendations</h4>
              <ul className="recommendations-list">
                {result.recommendations.map((recommendation, index) => (
                  <li key={index} className="recommendation-item">
                    {recommendation}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="verification-actions">
          <button className="btn btn-primary" onClick={onClose}>
            Continue Review
          </button>
        </div>
      </div>
    </div>
  );
};